import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRollForwardReady } from "../../api";

const LABELS = {
  personal: "personal",
  corporate: "corporate",
  tslips: "T-slip",
  other: "other",
};

const DISMISS_KEY = "rollForwardNudgeDismissedAt";

const isManager = (user) =>
  ["MANAGER", "ADMIN"].includes(String(user?.role).toUpperCase());

export default function RollForwardNudge({ user }) {
  const [counts, setCounts] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isManager(user)) return;
    getRollForwardReady().then(setCounts).catch(() => setCounts(null));
  }, [user]);

  if (!isManager(user) || !counts || !counts.total) return null;

  // Dismissal is keyed to the count, so the banner comes back when the
  // situation changes but never nags about the same news twice. It is kept
  // in localStorage (not sessionStorage) so a dismissal survives closing the
  // tab — resurfacing identical news every new browser session would be a
  // bigger disturbance than a durable dismissal, not a smaller one.
  //
  // localStorage access can throw (locked-down browser, in-app webview with
  // storage disabled, private-mode quota issues, ...). There is no error
  // boundary anywhere in this app, so an uncaught render error here would
  // unmount the whole React root, not just this nudge. Guard it and default
  // to "not dismissed" on a read failure -- the safe direction, since the
  // alternative is silently hiding information.
  let stored = null;
  try {
    stored = window.localStorage.getItem(DISMISS_KEY);
  } catch {
    stored = null;
  }
  if (dismissed || String(counts.total) === stored) return null;

  // Ties resolve in LABELS declaration order (personal, corporate, tslips,
  // other) because Array.prototype.sort is stable — intentional, not
  // accidental.
  const [category, count] = Object.entries(LABELS)
    .map(([key, label]) => [label, counts[key] || 0])
    .sort((a, b) => b[1] - a[1])[0];

  const extra = counts.total - count;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, fontSize: 13, color: "#475569" }}>
      <span>
        {count} {category} task{count === 1 ? "" : "s"} are ready to roll forward
        {extra > 0 ? ` (${extra} more in other categories)` : ""}
      </span>
      <Link to="/settings/roll-forward" style={{ color: "#3730a3", fontWeight: 600 }}>
        Review
      </Link>
      <button
        aria-label="Dismiss"
        onClick={() => {
          // Same guard on write: a failure to persist should still dismiss
          // for the current session (component state), it just won't
          // survive a reload.
          try {
            window.localStorage.setItem(DISMISS_KEY, String(counts.total));
          } catch {
            // Not persisted -- fall through to the in-memory dismissal.
          }
          setDismissed(true);
        }}
        style={{ marginLeft: "auto", background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}
